<template>
  <div :class="{ 'doc-noscroll': panel.open, 'widget-mode': authStore.getIsWidgetMode }">
    <navigation-bar v-if="!authStore.getIsWidgetMode" :section="section" @openHelp="onOpenHelp" @openSettings="onOpenSettings" />

    <v-main>
      <router-view :key="$route.fullPath" />
    </v-main>

    <AppSidePanel
      v-model="panel.open"
      :component="panel.component"
      :component-props="panel.props"
      :mode="panel.mode"
      :fixed-width="panel.fixedWidth"
      :max-width="panel.maxWidth"
      @closed="onPanelClosed"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, type Component, type DefineComponent } from 'vue';
import AppSidePanel from '@/components/shared/AppSidePanel.vue';
import HelpPanel from '@/components/shared/HelpPanel.vue';
import SettingsPanel from '@/components/shared/SettingsPanel.vue';
import NavigationBar from '@/components/layout/navigation/NavigationBar.vue';
import { useAuthenticationStore } from '@/stores/authentication';

type PanelMode = 'fixed' | 'auto';

interface PanelState {
  open: boolean;
  component: Component | DefineComponent<any, any, any> | null;
  props: Record<string, unknown>;
  mode: PanelMode;
  fixedWidth: number | undefined;
  maxWidth: number | undefined;
}

export default defineComponent({
  name: 'AppLayout',
  props: ["section"],
  components: { AppSidePanel, NavigationBar },
  setup() {
    const authStore = useAuthenticationStore();
    return { authStore };
  },
  data(): { panel: PanelState; lastOpener: Element | null } {
    return {
      panel: {
        open: false,
        component: null,
        props: {},
        mode: 'fixed',
        fixedWidth: 0,
        maxWidth: 0
      },
      lastOpener: null
    };
  },
  methods: {
    onOpenHelp(payload: { opener?: Element | null; docId?: number | string } = {}) {
      this.openPanel({
        component: HelpPanel,
        props: { },
        mode: 'fixed',
        fixedWidth: 1300,
        maxWidth: 820,
        opener: payload.opener ?? (document.activeElement as Element | null)
      });
    },

    onOpenSettings(payload: { opener?: Element | null } = {}) {
      this.openPanel({
        component: SettingsPanel,
        props: {},
        mode: 'auto',
        opener: payload.opener ?? (document.activeElement as Element | null)
      });
    },

    openPanel(payload: {
      component: Component | DefineComponent<any, any, any> | null;
      props?: Record<string, unknown>;
      mode?: PanelMode;
      fixedWidth?: number | undefined;
      maxWidth?: number | undefined;
      opener?: Element | null;
    }) {
      const {
        component,
        props = {},
        mode = 'fixed',
        fixedWidth = 480,
        maxWidth = 900,
        opener = null
      } = payload;

      this.panel.component = component;
      this.panel.props = props;
      this.panel.mode = mode;
      this.panel.fixedWidth = fixedWidth;
      this.panel.maxWidth = maxWidth;
      this.lastOpener = opener;
      this.panel.open = true;
    },

    onPanelClosed() {
      this.panel.open = false;
      this.panel.component = null;
      this.panel.mode = 'auto';

      this.panel.props = {};
      if (this.lastOpener && typeof (this.lastOpener as HTMLElement).focus === 'function') {
        (this.lastOpener as HTMLElement).focus();
      }
      this.lastOpener = null;
    }
  }
});
</script>

<style lang="scss" scoped>
.widget-mode {
  :deep(.v-main) {
    padding-left: 0 !important;
  }
  
  :deep(.page-container) {
    margin-left: 12px !important;
    width: calc(100% - 24px) !important;
  }
}

@media only screen and (max-width: 959px) {
  .widget-mode :deep(.page-container) {
    margin-left: 0 !important;
    width: 100% !important;
  }
}
</style>
